// @ts-strict-ignore
import { populateThresholdData } from 'flow/service/thresholds';
import isEqual from 'lodash/isEqual';
import type { State } from '~/store';
import type { Snapshot } from '../types';


export function selectWorkflowSnapshotsWorkflowData(state: State) {
  return state.workflowEditor.workflowSnapshots.workflowData;
}

export function selectInitialWorkflowData(state: State) {
  return state.workflowEditor.workflowSnapshots.workflowData.history.initial?.data;
}

export function selectInitialWorkflowProps(state: State) {
  return state.workflowEditor.workflowSnapshots.workflowProps.history.initial?.data;
}

export function selectWorkflowDataSnapshotById(
  snapshotId: Snapshot<unknown>['snapshotId'],
) {
  return function selector(state: State) {
    const workflowDataSnapshots = selectWorkflowSnapshotsWorkflowData(state);

    if (workflowDataSnapshots.history.initial.snapshotId === snapshotId) {
      return workflowDataSnapshots.history.initial;
    }

    return workflowDataSnapshots.history.past.find(snapshot => (
      snapshot.snapshotId === snapshotId
    ));
  };
}

export function selectHasUnsavedChangesInWorkflowData(state: State) {
  const initialWorkflowData = selectInitialWorkflowData(state);
  const currentWorkflowData = state.workflowEditor.workflowData;
  // Treat undefined and null as equivalent to avoid false positives
  const normalizeNullish = (value: unknown) => (value == null ? null : value);

  return !isEqual(
    normalizeNullish(initialWorkflowData),
    normalizeNullish(currentWorkflowData),
  );
}

export function selectInitialActions(state: State) {
  const initialWorkflowData = selectInitialWorkflowData(state);
  return initialWorkflowData?.actions;
}

export function selectInitialThresholds(state: State) {
  const initialWorkflowData = selectInitialWorkflowData(state);
  return initialWorkflowData.thresholds;
}

export function selectInitialThresholdById(thresholdId: string) {
  return function selector(state: State) {
    const initialThresholds = selectInitialThresholds(state);
    return initialThresholds ? initialThresholds[thresholdId] : null;
  };
}

export function selectInitialPopulatedThresholdById(thresholdId: string) {
  return function selector(state: State) {
    const initialThreshold = selectInitialThresholdById(thresholdId)(state);
    const initialWorkflowData = selectInitialWorkflowData(state);
    const initialWorkflowProps = selectInitialWorkflowProps(state);

    return initialThreshold
      ? populateThresholdData({
        actions: initialWorkflowData.actions,
        reasonCodeGroups: initialWorkflowProps.reasonCodeGroups,
        services: initialWorkflowProps.services,
        threshold: initialThreshold,
        universalAttributes: initialWorkflowProps.universalAttributes,
      })
      : null;
  };
}

export function selectCurrentWorkflowHistoryEntry(state: State) {
  const { historyIndex } = state.workflowEditor.editHistory;
  const { history } = state.workflowEditor.editHistory;

  return history[historyIndex];
}

export function selectHasHistory(state: State) {
  const { history } = state.workflowEditor.editHistory;
  return history.length > 0;
}

export function selectCanUndo(state: State) {
  const { historyIndex } = state.workflowEditor.editHistory;

  return selectHasHistory(state) && historyIndex > 0;
}

export function selectCanRedo(state: State) {
  const { history, historyIndex } = state.workflowEditor.editHistory;

  return selectHasHistory(state) && historyIndex < history.length - 1;
}
